/**
 * Intelly Conversation Step — the personalisation act.
 *
 * Not a form. Not a wizard. A single flowing conversation that saves
 * every answer as the customer types. Each question is a chat bubble
 * from Intelly and a soft text field from the customer. No progress
 * bar — the whole surface is intentionally quiet.
 *
 * Questions can be answered in any order; skipping is fine. When the
 * customer indicates they're ready, we ship whatever answers exist.
 */
import { useState } from "react";
import { Button } from "@/shared/ui/button";
import type { IntellyAnswers } from "@/entities/genesis";

interface Props {
  initial: IntellyAnswers;
  onNext: (answers: IntellyAnswers) => Promise<void> | void;
  onBack: () => void;
}

type Q = {
  key: keyof IntellyAnswers;
  prompt: string;
  placeholder: string;
  multiline?: boolean;
  toggle?: boolean;
};

const QUESTIONS: Q[] = [
  { key: "business_name", prompt: "First — what's the business called?", placeholder: "e.g. Iron Wren Coffee Bar" },
  { key: "location", prompt: "Where are you based?", placeholder: "e.g. Austin, TX" },
  { key: "service_area", prompt: "Anywhere you don't serve?", placeholder: "e.g. within a 20-mile radius", multiline: true },
  { key: "products_services", prompt: "What do you actually sell? Short answer is fine.", placeholder: "e.g. weekly lawn care with occasional aeration + overseed", multiline: true },
  { key: "ideal_customer", prompt: "Who's the person you love working with?", placeholder: "e.g. homeowner who cares about how the street looks", multiline: true },
  { key: "brand_personality", prompt: "If your business were a person, how would they talk?", placeholder: "e.g. warm neighbour, quietly proud of good work", multiline: true },
  { key: "mission", prompt: "In one sentence — why does this business exist?", placeholder: "e.g. give homeowners a yard they'd be proud to sit in on a Sunday", multiline: true },
  { key: "website", prompt: "Website? (optional)", placeholder: "https://" },
  { key: "additional_context", prompt: "Anything else you'd like me to know?", placeholder: "The stuff you'd tell a new hire on day one.", multiline: true },
  { key: "has_logo", prompt: "Do you already have a logo?", placeholder: "", toggle: true },
];

export function IntellyConversationStep({ initial, onNext, onBack }: Props): JSX.Element {
  const [ans, setAns] = useState<IntellyAnswers>({ ...initial });
  const [submitting, setSubmitting] = useState(false);

  const update = <K extends keyof IntellyAnswers>(k: K, v: IntellyAnswers[K]) => {
    setAns((prev) => ({ ...prev, [k]: v }));
  };

  return (
    <div className="mx-auto max-w-3xl space-y-10 pb-16" data-testid="intelly-conversation-step">
      <div className="space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c7a25a]">Conversation</p>
        <h2 className="font-serif text-3xl leading-tight text-[#f5efe6] md:text-4xl" style={{ fontFamily: "'Fraunces', serif" }}>
          Tell me about your business.
        </h2>
        <p className="max-w-lg text-[15px] leading-relaxed text-[#f5efe6]/70">
          Take your time. Skip anything that doesn't apply. Your answers
          shape everything — you're not filling in a form, you're teaching
          your marketing department how you think.
        </p>
      </div>

      <div className="space-y-6">
        {QUESTIONS.map((q) => (
          <ConversationTurn key={q.key} question={q} value={ans[q.key]} onChange={(v) => update(q.key, v as never)} />
        ))}
      </div>

      <div className="flex flex-col items-start gap-3">
        <button type="button" onClick={onBack} data-testid="intelly-conversation-back" className="text-[13px] text-[#f5efe6]/65 hover:text-[#f5efe6]">
          ← Back to preview
        </button>
        <Button
          onClick={async () => {
            setSubmitting(true);
            try { await onNext(ans); } finally { setSubmitting(false); }
          }}
          data-testid="intelly-conversation-continue"
          disabled={submitting}
          className="h-13 rounded-full bg-[#c7a25a] px-8 text-[15px] font-medium text-[#0d1310] hover:bg-[#d4b06e] disabled:opacity-40"
        >
          {submitting ? "Thinking…" : "That's a good start →"}
        </Button>
        <p className="text-[12px] text-[#f5efe6]/40">
          You'll see everything before it's saved. Nothing is public.
        </p>
      </div>
    </div>
  );
}

function ConversationTurn({ question, value, onChange }: {
  question: Q; value: unknown; onChange: (v: unknown) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-[42px_1fr]">
      <div className="hidden md:block">
        <div className="grid h-10 w-10 place-items-center rounded-full border border-[#c7a25a]/40 bg-[#c7a25a]/12 font-serif italic text-[#c7a25a]" style={{ fontFamily: "'Fraunces', serif" }}>
          i
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-[14px] font-medium text-[#f5efe6]" data-testid={`intelly-q-${question.key}`}>
          {question.prompt}
        </p>
        {question.toggle ? (
          <div className="flex gap-2" data-testid={`intelly-toggle-${question.key}`}>
            <ToggleChip active={value === true} label="Yes, I have one" onClick={() => onChange(true)} />
            <ToggleChip active={value === false} label="Not yet — let's design one" onClick={() => onChange(false)} />
          </div>
        ) : question.multiline ? (
          <textarea
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            rows={3}
            data-testid={`intelly-input-${question.key}`}
            className="w-full resize-none rounded-2xl border border-[#f5efe6]/10 bg-[#131a17] px-4 py-3 text-[14px] leading-relaxed text-[#f5efe6] placeholder:text-[#f5efe6]/30 focus:border-[#c7a25a]/60 focus:outline-none focus:ring-2 focus:ring-[#c7a25a]/20"
          />
        ) : (
          <input
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={question.placeholder}
            data-testid={`intelly-input-${question.key}`}
            className="w-full rounded-full border border-[#f5efe6]/10 bg-[#131a17] px-4 py-3 text-[14px] text-[#f5efe6] placeholder:text-[#f5efe6]/30 focus:border-[#c7a25a]/60 focus:outline-none focus:ring-2 focus:ring-[#c7a25a]/20"
          />
        )}
      </div>
    </div>
  );
}

function ToggleChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-[13px] transition ${
        active
          ? "border-[#c7a25a] bg-[#c7a25a]/15 text-[#c7a25a]"
          : "border-[#f5efe6]/10 text-[#f5efe6]/70 hover:border-[#c7a25a]/40 hover:text-[#f5efe6]"
      }`}
    >
      {label}
    </button>
  );
}
